import { seriesStyle } from './useChartLayout';
import './charts.css';

/**
 * The frame every chart sits in, so all of them behave identically: title,
 * optional subtitle, an explicit note when the backend cannot span the
 * selected range, legend, and the empty / loading states.
 *
 * `span` maps to the grid-span classes in charts.css — a 24-column heatmap or a
 * polar plot needs more than one track to be readable.
 *
 * On refetch the card holds its previous render at reduced opacity rather than
 * swapping in a skeleton: no layout jump, no flash, and the reader can still
 * read the old numbers while the new ones land.
 */
function ChartCard({
  title,
  subtitle,
  note,
  description,     // what the chart contains and how to read it
  legend,          // [{ name, slot }]
  loading = false,
  empty = false,
  emptyText = 'No data',
  span,            // '2' | 'full'
  children,
}) {
  const spanClass = span ? ` chart-card-span-${span}` : '';
  const loadingClass = loading ? ' chart-card-loading' : '';

  return (
    <figure className={`chart-card${spanClass}${loadingClass}`}>
      <figcaption className="chart-card-head">
        <span className="chart-card-title">{title}</span>
        {subtitle && <span className="chart-card-subtitle">{subtitle}</span>}
        {/* Never silently truncate: if the query could not cover the selected
            range, the card says so. */}
        {note && <span className="chart-card-note">{note}</span>}
      </figcaption>

      {empty && !loading ? (
        <div className="chart-card-empty">{emptyText}</div>
      ) : (
        <div className="chart-card-body">{children}</div>
      )}

      {/* The subtitle says what is plotted; this says what it MEANS and what a
          reader should conclude from it. Sits below the plot so it reads as a
          caption rather than competing with the title for the top of the card. */}
      {description && <p className="chart-card-description">{description}</p>}

      {legend && legend.length > 1 && (
        <div className="chart-card-legend">
          {legend.map((s, i) => {
            const { color, dash } = seriesStyle(s.slot != null ? s.slot : i + 1);
            return (
              <span className="chart-legend-item" key={s.name}>
                <svg className="chart-legend-key" viewBox="0 0 18 8" aria-hidden="true">
                  {/* Mirror the mark: a line for line charts, a bar for bars. */}
                  {s.mark === 'bar' ? (
                    <rect x="1" y="1" width="16" height="6" rx="1" fill={color} />
                  ) : (
                    <line x1="1" y1="4" x2="17" y2="4" stroke={color}
                          strokeWidth="2" strokeDasharray={dash} />
                  )}
                </svg>
                {s.name}
              </span>
            );
          })}
        </div>
      )}
    </figure>
  );
}

export default ChartCard;
